/**
 * Agentic screening control panel (superadmin only).
 *
 * Picks a published workflow, prefills the argument form from the version's JSON
 * Schema, triggers a run and follows it live. The dashboard talks only to
 * clinical-trials; that service authorises the superadmin and proxies to the
 * agent-orchestration service, which has no ingress of its own.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import { RunLog } from "../components/screening/RunLog";
import { RunResultsTable } from "../components/screening/RunResultsTable";
import { useScreeningRun } from "../hooks/useScreeningRun";
import type {
  ScreeningWorkflowDetail,
  ScreeningWorkflowSummary,
} from "../api/clinicalTrialsApiTypes";

// Injected by the runner for every session; never sent by a caller.
const RESERVED = ["run_id", "output_dir", "row_path"];

/**
 * A placeholder value of the right JSON type for an argument with no default.
 *
 * @param type - JSON Schema type name
 * @returns An empty value of that type
 */
function blank(type: string | undefined): unknown {
  if (type === "array") return [];
  if (type === "integer" || type === "number") return 0;
  if (type === "boolean") return false;
  return "";
}

/**
 * Build the prefilled argument JSON for a workflow version, so nobody has to
 * remember argument names. Optional arguments with no default are left out.
 *
 * @param detail - The workflow version
 * @returns Pretty-printed JSON for the arguments box
 */
function skeleton(detail: ScreeningWorkflowDetail): string {
  const properties = detail.arguments_schema.properties ?? {};
  const required = detail.arguments_schema.required ?? [];
  const out: Record<string, unknown> = {};
  for (const [key, schema] of Object.entries(properties)) {
    if (RESERVED.includes(key)) continue;
    if (!required.includes(key) && schema.default === undefined) continue;
    out[key] =
      schema.default !== undefined ? schema.default : blank(schema.type);
  }
  return JSON.stringify(out, null, 2);
}

/**
 * Format an elapsed millisecond span as `12s` / `4m 03s`.
 *
 * @param ms - Elapsed milliseconds
 * @returns Human-readable duration
 */
function elapsed(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;
}

/**
 * The screening page.
 *
 * @returns The screening control panel
 */
export function ScreeningPage() {
  const { user } = useAuth();
  const isSuperAdmin = Boolean(user?.isSuperAdmin);

  const [workflows, setWorkflows] = useState<ScreeningWorkflowSummary[]>([]);
  const [selected, setSelected] = useState("");
  const [version, setVersion] = useState<number | undefined>(undefined);
  const [detail, setDetail] = useState<ScreeningWorkflowDetail | null>(null);
  const [argsText, setArgsText] = useState("{}");
  const [submitting, setSubmitting] = useState(false);
  const [attachId, setAttachId] = useState("");
  const [tick, setTick] = useState(0);
  const run = useScreeningRun();

  const loadWorkflows = useCallback(async () => {
    try {
      const rows = await clinicalTrialsService.listScreeningWorkflows();
      setWorkflows(rows);
      setSelected((current) => current || rows[0]?.name || "");
    } catch (err) {
      toast.error(
        (err as Error)?.message || "Could not list workflows",
        "Error",
      );
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) void loadWorkflows();
  }, [isSuperAdmin, loadWorkflows]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    void clinicalTrialsService
      .getScreeningWorkflow(selected, version)
      .then((found) => {
        if (cancelled) return;
        setDetail(found);
        setArgsText(skeleton(found));
      })
      .catch((err: Error) =>
        toast.error(err.message, "Could not load workflow"),
      );
    return () => {
      cancelled = true;
    };
  }, [selected, version]);

  // Drives the elapsed-time readout while a run is streaming.
  useEffect(() => {
    if (!run.streaming) return;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [run.streaming]);

  const versions = useMemo(
    () =>
      detail
        ? Array.from(
            { length: detail.latest_version },
            (_, i) => detail.latest_version - i,
          )
        : [],
    [detail],
  );

  if (!isSuperAdmin) return <Navigate to="/notifications" replace />;

  const handleTrigger = async () => {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(argsText || "{}") as Record<string, unknown>;
    } catch (err) {
      toast.error((err as Error).message, "Arguments are not valid JSON");
      return;
    }
    setSubmitting(true);
    try {
      const started = await clinicalTrialsService.triggerScreeningRun({
        workflow: selected,
        version: version ?? null,
        arguments: parsed,
      });
      run.follow(started.run_id);
      toast.success(`Run ${started.run_id} accepted`, "Screening started");
    } catch (err) {
      toast.error((err as Error)?.message || "Failed to start run", "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const total = run.counters.items || 1;
  const percent = Math.round((run.counters.done / total) * 100);

  return (
    <div className="mx-auto max-w-6xl p-8">
      <h1 className="text-2xl font-bold mb-1">Agentic Screening</h1>
      <p className="text-sm text-gray-500 mb-6">
        Run a published prescreening workflow over a patient cohort. A run reads
        patient documents and costs model spend, so triggering is
        superadmin-only.
      </p>

      <div className="rounded-lg border border-gray-200 p-6 mb-6 bg-white">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-1">Workflow</label>
            <select
              value={selected}
              onChange={(e) => {
                setSelected(e.target.value);
                setVersion(undefined);
              }}
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
            >
              {workflows.map((row) => (
                <option key={row.name} value={row.name}>
                  {row.name} — v{row.latest_version}
                </option>
              ))}
            </select>
            {detail?.description && (
              <p className="text-xs text-gray-500 mt-1">{detail.description}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Version</label>
            <select
              value={version ?? detail?.version ?? ""}
              onChange={(e) => setVersion(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
            >
              {versions.map((n) => (
                <option key={n} value={n}>
                  v{n}
                  {detail && n === detail.latest_version ? " (latest)" : ""}
                </option>
              ))}
            </select>
            {detail && (
              <p className="text-xs text-gray-500 mt-1">
                {detail.spec.model} · {detail.spec.max_turns} turns ·{" "}
                {detail.spec.fan_out
                  ? `fan out ${detail.spec.fan_out} × ${detail.spec.concurrency}`
                  : "single session"}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium mb-1">
            Arguments (JSON)
          </label>
          <textarea
            value={argsText}
            onChange={(e) => setArgsText(e.target.value)}
            rows={10}
            spellCheck={false}
            className="w-full border border-gray-300 rounded-md p-3 text-sm font-mono"
          />
        </div>

        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={handleTrigger}
            disabled={submitting || !selected}
            className="px-5 py-2.5 rounded-md text-sm font-semibold text-white"
            style={{
              backgroundColor: "var(--color-primary)",
              opacity: submitting || !selected ? 0.6 : 1,
              cursor: submitting || !selected ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Starting…" : "Run Screening"}
          </button>
          {run.streaming && (
            <button
              onClick={run.stop}
              className="px-4 py-2 rounded-md text-sm border border-gray-300"
              style={{ cursor: "pointer" }}
            >
              Detach
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <input
              value={attachId}
              onChange={(e) => setAttachId(e.target.value)}
              placeholder="Attach to run id"
              className="border border-gray-300 rounded-md p-2 text-sm font-mono w-64"
            />
            <button
              onClick={() => attachId.trim() && run.follow(attachId.trim())}
              className="px-4 py-2 rounded-md text-sm border border-gray-300"
              style={{ cursor: "pointer" }}
            >
              Follow
            </button>
          </div>
        </div>
      </div>

      {run.runId && (
        <div className="rounded-lg border border-gray-200 p-6 mb-6 bg-white">
          <div className="flex flex-wrap items-baseline gap-4 mb-3">
            <span className="font-mono text-sm">{run.runId}</span>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase">
              {run.state || "…"}
            </span>
            <span className="text-sm text-gray-600">
              {run.counters.done} / {run.counters.items || 1} items
            </span>
            <span className="text-sm text-gray-600">
              {run.counters.turns} turns
            </span>
            <span className="text-sm text-gray-600">
              ${run.counters.cost.toFixed(2)}
            </span>
            <span className="text-sm text-gray-600" data-tick={tick}>
              {run.startedAt ? elapsed(Date.now() - run.startedAt) : ""}
            </span>
          </div>
          <div className="h-1.5 w-full rounded bg-gray-100 mb-4">
            <div
              className="h-1.5 rounded"
              style={{
                width: `${percent}%`,
                backgroundColor: "var(--color-primary)",
              }}
            />
          </div>
          {run.error && (
            <p className="text-sm text-red-600 mb-3">{run.error}</p>
          )}
          {run.status?.error && (
            <p className="text-sm text-red-600 mb-3">{run.status.error}</p>
          )}
          <RunLog events={run.events} />
        </div>
      )}

      {run.results && (
        <div className="rounded-lg border border-gray-200 p-6 bg-white">
          <h2 className="text-lg font-semibold mb-3">Results</h2>
          <RunResultsTable results={run.results} />
        </div>
      )}
    </div>
  );
}
